<template>
  <div :id="controllerIdString"
       ref="controller"
       class="bg-light user-select-none d-flex flex-wrap align-items-center p-2"
       tabindex="0">

    <div ref="seekbarContainer"
         class="__seekbar_container  position-relative d-flex align-items-center px-2 flex-grow-1 w-100"
         role="button"
         tabindex="0">

      <div ref="seekbar"
           class="__seekbar  position-relative w-100 border bg-secondary rounded-3 overflow-hidden"
           :style="bufferedSeekbarStyle">

        <div class="position-absolute top-0 bottom-0 start-0 end-0"
             :style="playedSeekbarStyle">
        </div>

        <div ref="seekbarCurrent"
             class="__seekbar_current  position-absolute top-0 bottom-0 start-0"
             :style="currentSeekbarStyle">
        </div>

      </div>

      <div ref="seekbarControlContainer"
           :class="{'position-fixed': graspingSeekbar, }"
           class="__seekbar_control_container top-0 end-0 bottom-0 start-0">
      </div>

      <div ref="seekbarCursor"
           class="__seekbar_cursor  position-absolute"
           :style="seekbarCursorStyle">
      </div>

    </div>

    <div class="d-flex align-items-center">
      <button type="button" class="btn py-0" v-on:click="toggleStatus">
        <svgicon :name="statusIconName" />
      </button>
      <span class="small d-inline-block mx-2 my-1">
        {{toHms(currentTime)}}
        /
        {{toHms(duration)}}
      </span>
      <div class="__volume_container  d-flex position-relative"
           ref="volumeContainer">
        <button type="button" class="__volume_button  btn py-0" v-on:click="toggleMute">
          <svgicon :name="volumeIconName" />
        </button>
        <div class="__volume_seek_container  bg-light d-flex align-items-center p-2 border rounded-3"
             tabindex="0">
          <input type="range"
                 min="0"
                 max="1"
                 step="0.01"
                 v-model="volumeValue"
                 v-on:change="volumeChange()"
                 class="__volume_seek"
                 ref="volumeSeekbar" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .__volume_seek {
    height: 0.35rem;
  }

  .__seekbar_container {
    height: 1.5rem;
    touch-action: none;
  }
  .__seekbar {
    height: 0.5rem;
  }

  .__seekbar_cursor {
    height: 1rem;
    width: 1rem;
    border-radius: 1rem;
    visibility: hidden;
  }

  .__seekbar_container:hover .__seekbar_cursor {
    visibility: visible;
  }
</style>

<script>
  import { defineComponent } from "vue";

  export default defineComponent({
    props: {
      duration: {
        type: Number,
        required: true,
      },
      currentTime: {
        type: Number,
        default: null,
      },
      muted: {
        type: Boolean,
        default: false,
      },
      volume: {
        type: Number,
        validator: (val) => {
          return 0 <= val && val <= 1;
        }
      },
      color: {
        type: String,
        default: "primary",
        validator: (val) => {
          return Object.keys(__COLOR_CODE).includes(val);
        },
      },
      played: {
        type: Array,
        validator: (val) => {
          return val.every(i => Object.keys(i).includes("start") && Object.keys(i).includes("end"));
        }
      },
      buffered: {
        type: Array,
        validator: (val) => {
          return val.every(i => Object.keys(i).includes("start") && Object.keys(i).includes("end"));
        }
      },
      status: {
        type: String,
        default: "paused",
        validator: (val) => {
          return ["playing", "paused", "ended"].includes(val);
        },
      },
    },
    computed: {
      statusIconName() {
        switch (this.status) {
          case "playing":
            return "pause";
          case "ended":
            return "arrow_clockwise";
          default:
            return "play";
        };
      },
      volumeIconName() {
        if (this.muted) return "volume_mute";
        if (this.volume === 0) return "volume_off";
        if (0 < this.volume && this.volume <= 0.5) return "volume_down";
        if (0.5 < this.volume) return "volume_up";
        return "volume_off";
      },
      currentSeekbarStyle() {
        return `background-color: ${__COLOR_CODE[this.color]}; width: ${this.convertToPercent(this.currentTime, 2)}%`;
      },
      playedSeekbarStyle() {
        if (this.played == null || this.played.length == 0) return null;
        let s = "background: linear-gradient(to right,";
        this.played.forEach((r, index) => {
          if (index === 0) {
            if (r.start === 0) {
              s += ` ${__COLOR_CODE[this.color]}4d 0%, ${__COLOR_CODE[this.color]}4d ${this.convertToPercent(r.end)}%`;
            } else {
              s += ` transparent 0%, transparent ${this.convertToPercent(r.start)}%`;
              s += `, ${__COLOR_CODE[this.color]}4d ${this.convertToPercent(r.start)}%, ${__COLOR_CODE[this.color]}4d ${this.convertToPercent(r.end)}%`;
            }
          } else {
            s += ` transparent ${this.convertToPercent(r.start)}%,`
            s += ` ${__COLOR_CODE[this.color]}4d ${this.convertToPercent(r.start)}%, ${__COLOR_CODE[this.color]}4d ${this.convertToPercent(r.end)}%`;
          }

          if (index === this.played.length - 1) {
            if (r.end !== this.duration) {
              s += `, transparent ${this.convertToPercent(r.end)}%, transparent 100%`;
            }
          } else {
            s += `, transparent ${this.convertToPercent(r.end)}%,`;
          }
        });
        return s += ");";
      },
      seekbarCursorStyle() {
        if (this.seekbarCurrent == null) return null;
        let visible = this.graspingSeekbar ? "visibility: visible;" : null;
        return `background-color: ${__COLOR_CODE[this.color]}; left: ${this.seekbarCursorPosition}px; ${visible}`;
      },
      bufferedSeekbarStyle() {
        if (this.buffered == null || this.buffered.length == 0) return null;
        let s = "background: linear-gradient(to right,";
        this.buffered.forEach((r, index) => {
          if (index === 0) {
            if (r.start === 0) {
              s += ` #FFFFFF4d 0%, #FFFFFF4d ${this.convertToPercent(r.end)}%`;
            } else {
              s += ` transparent 0%, transparent ${this.convertToPercent(r.start)}%`;
              s += `, #FFFFFF4d ${this.convertToPercent(r.start)}%, #FFFFFF4d ${this.convertToPercent(r.end)}%`;
            }
          } else {
            s += ` transparent ${this.convertToPercent(r.start)}%,`
            s += ` #FFFFFF4d ${this.convertToPercent(r.start)}%, #FFFFFF4d ${this.convertToPercent(r.end)}%`;
          }

          if (index === this.buffered.length - 1) {
            if (r.end !== this.duration) {
              s += `, transparent ${this.convertToPercent(r.end)}%, transparent 100%`;
            }
          } else {
            s += `, transparent ${this.convertToPercent(r.end)}%,`;
          }
        });
        return s += ");";
      },
    },
    data: () => {
      return {
        controller: null,
        controllerIdString: null,
        seekbar: null,
        seekbarContainer: null,
        seekbarBuffered: null,
        seekbarCurrent: null,
        seekbarCursor: null,
        seekbarControlContainer: null,
        volumeContainer: null,
        volumeSeekbar: null,
        seekbarCursorPosition: 0,
        graspingSeekbar: false,
        seekbarPercentage: 0,
        volumeValue: 0,
      }
    },
    methods: {
      toHms(t) {
        t = t < 0 ? 0 : t;
        let zeroPadding = (v) => {
          return v < 10 ? "0" + v : v;
        };
        let h = Math.floor(t / 3600) || 0;
        let m = Math.floor(t % 3600 / 60) || 0;
        let s = Math.floor(t % 60);
        if (h != 0) return h + ":" + zeroPadding(m) + ":" + zeroPadding(s);
        return m + ":" + zeroPadding(s);
      },
      convertToPercent(val, digit = 0) {
        let cv = Math.floor(val / this.duration * 100 * Math.pow(10, digit)) / Math.pow(10, digit);
        return cv < 0 ? 0 : 100 < cv ? 100 : cv;
      },
      convertToSecond(percentVal, digit = 0) {
        let cv = Math.ceil(this.duration * percentVal / 100 * Math.pow(10, digit)) / Math.pow(10, digit);
        return cv < 0 ? 0 : this.duration < cv ? this.duration : cv;
      },
      calculateCurrentTime(e) {
        let scr = this.seekbar.getBoundingClientRect();

        let o = 0;
        switch (e.target) {
          case this.seekbarContainer:
            let containerRect = this.seekbarContainer.getBoundingClientRect();
            o = e.offsetX - ((containerRect.width - scr.width) - (scr.left - containerRect.left));
            break;
          case this.seekbarControlContainer:
            o = e.offsetX - scr.left;
            break;
          case this.seekbarCursor:
            let currentWidth = this.seekbarCurrent.getBoundingClientRect().width;
            let cursorRect = this.seekbarCursor.getBoundingClientRect();
            o = currentWidth + (e.offsetX - (cursorRect.width / 2));
            break;
          default:
            o = e.offsetX;
        }

        o = o < 0 ? 0 : scr.width < o ? scr.width : o;

        return this.convertToSecond((o / scr.width * 100), 2);
      },
      calculateCursorPosition() {
        let sccr = this.seekbarCurrent.getBoundingClientRect();
        this.seekbarCursorPosition = sccr.width;
      },
      toggleStatus() {
        if (this.status === "paused" || this.status === "ended") {
          this.$emit("play");
        } else {
          this.$emit("pause");
        }
      },
      toggleMute() {
        this.$emit("volumeChanged", { muted: !this.muted, volume: parseFloat(this.volumeValue), });
      },
      volumeChange() {
        this.$emit("volumeChanged", { muted: this.muted, volume: parseFloat(this.volumeValue), });
      }
    },
    beforeCreate() {
    },
    created() {
      this.controllerIdString = crypto.randomUUID();
    },
    mounted() {
      this.controller = this.$refs.controller;
      this.seekbar = this.$refs.seekbar;
      this.seekbarCurrent = this.$refs.seekbarCurrent;
      this.seekbarCursor = this.$refs.seekbarCursor;
      this.seekbarContainer = this.$refs.seekbarContainer;
      this.seekbarControlContainer = this.$refs.seekbarControlContainer;

      this.volumeContainer = this.$refs.volumeContainer;
      this.volumeSeekbar = this.$refs.volumeSeekbar;
      this.volumeValue = this.volume;

      this.seekbarContainer.addEventListener("pointermove", function (e) {
        let event = e.getCoalescedEvents();
        if (!this.graspingSeekbar) return;
        this.$emit("seeking", this.calculateCurrentTime(...event));
      }.bind(this));

      this.seekbarContainer.addEventListener("pointerdown", function (e) {
        this.graspingSeekbar = true;
        this.$emit("seekStart", this.calculateCurrentTime(e));
        window.addEventListener("pointerup", function (e) {
          if (!this.graspingSeekbar) return;
          this.graspingSeekbar = false;
          this.$emit("seekEnd");
        }.bind(this)
          , { once: true });

      }.bind(this));
    },
    emits: ["volumeChanged", "seekStart", "seeking", "seekEnd", "play", "pause"],
    watch: {
      currentTime: function () {
        this.$nextTick(function () {
          this.calculateCursorPosition();
        });
      }
    }
  });

</script>
